<template>
  <!-- Okno tworzenia grupy poszukiwanych (czarna/biała lista) z panelu użytkowników.
       Tylko warianty SG/NOSG/POSG — o widoczności decyduje rodzic (`UserList`). -->
  <div v-if="isVisible" class="modal-overlay" @click.self="close">
    <div class="modal" role="dialog" aria-modal="true" :aria-label="t('create-user-group-title')">
      <header class="modal-header">
        <h2 class="margin-0">{{ t('create-user-group-title') }}</h2>
        <button type="button" class="close-button" :title="t('cancel')" @click="close">×</button>
      </header>

      <form class="modal-body" @submit.prevent="submit">
        <div class="form-group">
          <label for="wanted-group-name">{{ t('user-group-name') }} *</label>
          <input
            id="wanted-group-name"
            ref="nameInput"
            v-model="form.name"
            type="text"
            maxlength="255"
            required
          />
        </div>

        <div class="form-group">
          <label for="wanted-group-description">{{ t('description') }}</label>
          <input id="wanted-group-description" v-model="form.description" type="text" maxlength="255" />
        </div>

        <div class="form-group">
          <span class="group-label">{{ t('user-contact-groups-channel') }}</span>
          <div class="channel">
            <label v-for="option in contactTypes" :key="option.value" class="channel-item">
              <input v-model.number="form.typeOfContact" type="radio" :value="option.value" />
              <span>{{ option.label }}</span>
            </label>
          </div>
        </div>

        <div class="form-group">
          <div class="members-header">
            <span class="group-label">{{ t('user-group-members') }}</span>
            <span class="members-count">{{ t('user-group-selected-count', { count: form.userIds.length }) }}</span>
          </div>
          <input v-model="memberQuery" type="text" :placeholder="t('search-user')" />
          <ul class="members">
            <li v-for="user in visibleUsers" :key="user.id">
              <label
                class="member"
                :class="{ 'member--disabled': !canPick(user) }"
                :title="canPick(user) ? '' : t('user-out-of-your-hierarchy')"
              >
                <input
                  v-model="form.userIds"
                  type="checkbox"
                  :value="user.id"
                  :disabled="!canPick(user)"
                />
                <span class="member-login">{{ user.username }}</span>
                <span class="member-name">{{ fullName(user) }}</span>
              </label>
            </li>
            <li v-if="visibleUsers.length === 0" class="members-empty">{{ t('no-results') }}</li>
          </ul>
          <small class="hint">{{ t('user-group-members-hint') }}</small>
        </div>

        <div class="modal-actions">
          <button type="button" class="btn" @click="close">{{ t('cancel') }}</button>
          <button type="submit" class="btn button-add-user" :disabled="!canSubmit">
            {{ t('create-user-group') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import Users from "@/services/Users";
import { useNotificationStore } from "@/stores/notification.store";
import { useUserStore } from "@/stores/user.store";

// Stan startowy formularza — kanał e-mail jak w formularzu dodawania użytkownika (`AddUser`).
const emptyForm = () => ({ name: "", description: "", typeOfContact: 0, userIds: [] });

export default {
  name: "CreateWantedGroupModal",
  props: {
    isVisible: { type: Boolean, required: true },
    // Użytkownicy z listy panelu (te same obiekty, co w tabeli).
    users: { type: Array, default: () => [] },
    // Użytkownicy zaznaczeni w tabeli — wstępnie wybrani przy otwarciu okna.
    preselectedIds: { type: Array, default: () => [] },
  },
  emits: ["close", "created"],
  setup() {
    const { t } = useI18n();
    return { t, userStore: useUserStore() };
  },
  data() {
    return {
      form: emptyForm(),
      memberQuery: "",
      isSubmitting: false,
    };
  },
  computed: {
    contactTypes() {
      return [
        { value: 0, label: this.t("email") },
        { value: 1, label: this.t("contact-type-sms") },
        { value: 2, label: this.t("contact-type-mms") },
      ];
    },
    // Lista w oknie: filtr po loginie/imieniu/nazwisku, alfabetycznie po loginie.
    visibleUsers() {
      const terms = this.memberQuery.toLowerCase().trim().split(/\s+/).filter(Boolean);

      return this.users
        .filter((user) => {
          const haystack = [user.username, user.firstName, user.lastName]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return terms.every((term) => haystack.includes(term));
        })
        .sort((a, b) => (a.username || "").localeCompare(b.username || "", "pl", { sensitivity: "base" }));
    },
    canSubmit() {
      return !this.isSubmitting && this.form.name.trim() !== "";
    },
  },
  watch: {
    // Każde otwarcie zaczyna od czystego formularza z użytkownikami zaznaczonymi w tabeli.
    isVisible(visible) {
      if (!visible) {
        return;
      }

      this.form = emptyForm();
      this.form.userIds = this.preselectedIds.filter((id) => {
        const user = this.users.find((it) => it.id === id);
        return user && this.canPick(user);
      });
      this.memberQuery = "";
      this.$nextTick(() => this.$refs.nameInput?.focus());
    },
  },
  methods: {
    // Backend przyjmuje tylko konta w zasięgu hierarchii (własne + niższe) — reszta wygaszona.
    canPick(user) {
      return this.userStore.canEditUser(user);
    },
    fullName(user) {
      return [user.firstName, user.lastName].filter(Boolean).join(" ");
    },
    close() {
      if (!this.isSubmitting) {
        this.$emit("close");
      }
    },
    async submit() {
      if (!this.canSubmit) {
        return;
      }

      this.isSubmitting = true;
      const notifications = useNotificationStore();
      const name = this.form.name.trim();

      try {
        const result = await Users.createWantedContactGroup({
          name,
          description: this.form.description.trim() || null,
          userIds: this.form.userIds.map(Number),
          typeOfContact: Number(this.form.typeOfContact),
        });

        // Konta bez e-maila/telefonu dla wybranego kanału backend pomija — trzeba to powiedzieć
        // wprost, inaczej admin szukałby ich potem w kolumnie „Grupy poszukiwanych".
        const skipped = (result?.skippedUserIds || [])
          .map((id) => this.users.find((it) => it.id === id)?.username || `#${id}`);

        if (skipped.length > 0) {
          notifications.push(this.t("user-group-created-skipped", { name, users: skipped.join(", ") }), "warning");
        } else {
          notifications.push(this.t("user-group-created", { name }), "success");
        }

        this.isSubmitting = false;
        this.$emit("created", result);
      } catch (error) {
        console.error(error);
        this.isSubmitting = false;
        // Kolizja nazwy (ten sam właściciel) → 409 z komunikatem ścieżki `name`.
        const message = String(error?.message ?? error ?? "");
        notifications.push(
          message.startsWith("name:") || error === 409 ? this.t("user-group-name-exists") : this.t("user-group-create-error"),
          "error"
        );
      }
    },
  },
};
</script>

<style scoped>
/* Wygląd jak w `TicketModal` — ten sam overlay, nagłówek i odstępy z tokenów design systemu. */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--spacing-lg);
  z-index: 9999;
}

.modal {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 560px;
  max-height: calc(100vh - 2 * var(--spacing-xl));
  background-color: var(--surface-primary);
  border: 1px solid var(--border-default);
  border-radius: var(--panel-radius);
  box-shadow: var(--shadow-overlay);
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--surface-secondary);
  border-bottom: 1px solid var(--border-default);
}

.modal-header h2 {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--text-primary);
}

.close-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--control-height-sm);
  height: var(--control-height-sm);
  background: none;
  border: 0;
  border-radius: var(--control-radius);
  color: var(--text-secondary);
  font-size: var(--font-size-xl);
  line-height: 1;
  cursor: pointer;
}

.close-button:hover {
  background: var(--surface-hover);
  color: var(--text-primary);
}

.modal-body {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
  overflow-y: auto;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  margin: 0;
}

label,
.group-label {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.form-group > input {
  width: 100%;
}

.channel {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-md);
}

.channel-item {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  color: var(--text-primary);
  cursor: pointer;
}

.members-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.members-count {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
}

.members {
  max-height: 260px;
  margin: 0;
  padding: 0;
  list-style: none;
  overflow-y: auto;
  border: 1px solid var(--border-default);
  border-radius: var(--control-radius);
}

.member {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-xs) var(--spacing-sm);
  color: var(--text-primary);
  cursor: pointer;
}

.member:hover {
  background: var(--surface-hover);
}

.member--disabled {
  color: var(--text-secondary);
  cursor: not-allowed;
}

.member-login {
  font-weight: var(--font-weight-medium);
}

.member-name {
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.members-empty {
  padding: var(--spacing-sm);
  color: var(--text-secondary);
}

.hint {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-sm);
}
</style>
